import { useEffect } from 'react';
import { Query, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { CHANNEL_STATUS } from '@/entities/channel';
import {
  channelByIdQuery,
  refetchWhileChannelSyncing,
} from '@/entities/channel/queries';
import { ApiError } from '@/shared/api/ApiError';
import { IChannelDetailsDto } from '@/shared/api/channels';
import { useNotifications } from '@/shared/ui/Notifications';

export function useChannelById(
  channelId?: string,
  options?: Omit<UseQueryOptions<IChannelDetailsDto>, 'queryFn' | 'queryKey'>
) {
  const { addNotification } = useNotifications();

  const { data, isError, error, ...otherQueryData } =
    useQuery<IChannelDetailsDto>({
      ...channelByIdQuery(channelId!),
      refetchInterval: refetchWhileChannelSyncing,
      ...options,
      enabled: options?.enabled ? options.enabled && !!channelId : !!channelId,
    });

  useEffect(() => {
    if (isError && error) {
      if (ApiError.isCanceledError(error)) return;
      addNotification({
        type: 'error',
        title: error.message,
        caption: error.data?.details,
      });
    }
  }, [addNotification, error, isError]);

  return { channelByIdData: data, isError, error, ...otherQueryData };
}

const TIMEOUT_FOR_GET_QR_LINK = 1000;
const TIMEOUT_FOR_UPDATE_AND_GET_STATUS_QR = 4000;

export const refetchToGetQrCode = (
  query: Query<IChannelDetailsDto>,
  channelId?: string
) => {
  const channelData = query.state.data;

  if (
    !channelData ||
    !channelId ||
    channelData.status === CHANNEL_STATUS.PASSWORD_REQUIRED
  ) {
    return false;
  }

  return !channelData.qr_url ? TIMEOUT_FOR_GET_QR_LINK : false;
};

export const refetchToUpdateQrCode = (
  query: Query<IChannelDetailsDto>,
  channelId?: string
) => {
  const channelData = query.state.data;

  if (
    !channelData ||
    !channelId ||
    channelData.status === CHANNEL_STATUS.PASSWORD_REQUIRED
  ) {
    return false;
  }

  return channelData.status === CHANNEL_STATUS.UNAUTHORIZED ||
    channelData.status === CHANNEL_STATUS.QR_IN_PROGRESS ||
    channelData.status === CHANNEL_STATUS.REQUIRED_SCAN_QR
    ? TIMEOUT_FOR_UPDATE_AND_GET_STATUS_QR
    : false;
};
